import { useEffect, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import styles from './thumbnail.module.css';
import { cx } from './cx';

/**
 * A 16:9 thumbnail with an optional corner badge. Renders props: the URL arrived from `pickImage` and
 * the badge text from `videoBadgeOf`, both in core.
 *
 * Deliberately **no `width`/`height` attributes** — see `thumbnail.module.css` for the measurement
 * that rule comes from. `loading="lazy"` because twelve of these sit below the fold on every page
 * that has any.
 */
export type ThumbnailBadgeKind = 'duration' | 'live' | 'upcoming' | 'unknown';

export interface ThumbnailProps {
  readonly src: string | null;
  /** Almost always `''`: the row's title is the accessible name, and the image repeats it. */
  readonly alt?: string;
  /** `title` is nullable rather than optional because that is the shape core's badge helpers return. */
  readonly badge?: {
    readonly kind: ThumbnailBadgeKind;
    readonly text: string;
    readonly title?: string | null;
  } | null;
  /**
   * 4b's watched-progress bar along the bottom edge, from `toListingProgress`.
   *
   * A fraction and a sentence, which is all this primitive is allowed to know: `ui/` is domain-free, and
   * "how far into the video" is a number here exactly the way the badge's text is a string. It is drawn
   * `aria-hidden`, which costs nothing — a card's thumbnail is already hidden from assistive technology
   * so that each row has one link, and the spoken half of this is the row's marker (`listing/model.ts`).
   */
  readonly progress?: {
    readonly fraction: number;
    readonly title: string;
    /**
     * Draw the fraction as it is, without the floor below. For a bar that follows something the
     * reader is watching move — the hover preview's position — where a floor would make the
     * bar and the picture disagree for the first few percent of every video.
     */
    readonly exact?: boolean;
  } | null;
  /**
   * Drawn over the image and **under** the badge and the bar — the grid preview's tile
   * (2026-08-02). A node this primitive knows nothing about, the `PlayerSurface` overlay's
   * precedent: `ui/` stays domain-free, and the bar keeps painting on top so a preview can sync it
   * rather than cover it.
   */
  readonly overlay?: ReactNode;
  /**
   * Passed through to the root span — the grid preview's hover clock lives in its caller
   * (`useGridPreview`), and the box is the element whose geometry the scrub strip is measured
   * against, so the handlers must sit here rather than on a wrapper with no box of its own.
   */
  readonly onPointerEnter?: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove?: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly className?: string;
}

const BADGE_CLASS: Readonly<Record<ThumbnailBadgeKind, string | undefined>> = {
  duration: undefined,
  live: styles.badgeLive,
  upcoming: styles.badgeUpcoming,
  // `unknown` never renders a badge — a live recommendation reports `lengthSeconds: 0`, and "0:00"
  // would be a claim where the truth is an absence.
  unknown: undefined,
};

/**
 * **One** retry, ~600 ms later, and both numbers come from the measurement rather than from taste
 * (closing the gap 2b left open).
 *
 * 2 of 40 thumbnails on a feed page fail on first request and answer **200 with the right
 * `content-length`** when re-fetched a second afterwards — the `/vi/` proxy being intermittent at about
 * the rate `/companion/*` is (API reference §3). So the failure is transient in the cases actually
 * observed, and drawing a permanent grey frame for it threw away a picture the instance would have
 * served on the next attempt.
 *
 * One retry rather than a loop: a page carries up to 40 of these, so a two-retry policy against an
 * instance that is refusing is 80 extra requests aimed at something already unwell, and these images
 * never reach the transport that would otherwise pace them. And the delay exists
 * because an immediate re-request is the same request: whatever was busy has to have a moment.
 */
const RETRY_DELAY_MS = 600;

interface Attempt {
  /** The `src` this state belongs to, so a re-render onto another video does not inherit its failure. */
  readonly src: string | null;
  /** Bumped to force a fresh element — see the `key` below. */
  readonly attempt: number;
  readonly failed: boolean;
}

export function Thumbnail({
  src,
  alt = '',
  badge,
  progress,
  overlay,
  onPointerEnter,
  onPointerLeave,
  onPointerMove,
  className,
}: ThumbnailProps) {
  const [state, setState] = useState<Attempt>({ src, attempt: 0, failed: false });
  // React's documented "adjust state when a prop changes" — cheaper than an effect, and without it the
  // first paint after a navigation shows the *previous* video's failure for one frame.
  if (state.src !== src) setState({ src, attempt: 0, failed: false });

  useEffect(() => {
    if (!state.failed || state.attempt > 0) return;
    const timer = setTimeout(
      () => setState((current) => (current.failed ? { ...current, attempt: 1, failed: false } : current)),
      RETRY_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [state.failed, state.attempt, state.src]);

  /*
   * An image that did not load is drawn as the same empty frame a missing one is, which is what the
   * reader sees during the pause before the retry and permanently after it. The alternative — the
   * browser's broken-image glyph — reads as "this video is broken" rather than "this picture is late".
   */
  const usable = src !== null && !state.failed;

  return (
    <span
      className={cx(styles.box, className)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerMove={onPointerMove}
    >
      {!usable ? (
        <span className={styles.blank} aria-hidden="true" />
      ) : (
        <img
          /*
           * The attempt number, so the retry is a *new element*. Re-assigning an identical `src` to the
           * same `<img>` is a no-op as far as React is concerned — nothing in the props changed — and no
           * second request would ever go out.
           */
          key={state.attempt}
          className={styles.image}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setState((current) => ({ ...current, failed: true }))}
        />
      )}
      {overlay}
      {badge == null || badge.kind === 'unknown' ? null : (
        <span className={cx(styles.badge, BADGE_CLASS[badge.kind])} title={badge.title ?? undefined}>
          {badge.text}
        </span>
      )}
      {progress == null ? null : (
        // Two elements rather than one gradient, so the played part can carry `min-width`: a video two
        // minutes into two hours is 1.7 %, which rounds to nothing at a card's width.
        <span className={styles.progress} title={progress.title} aria-hidden="true">
          <span
            className={cx(styles.progressPlayed, progress.exact === true && styles.progressExact)}
            style={{ width: `${Math.min(100, Math.max(0, progress.fraction * 100))}%` }}
          />
        </span>
      )}
    </span>
  );
}
